import { Controller } from "@hotwired/stimulus"

// The check before the layoff table filters, drawn as a dialog rather than as
// Cloudflare's own interstitial page.
//
// The gate is at Cloudflare, not here: a WAF rule challenges every request to
// /layoffs or /events with a query string, and a request that has not passed
// comes back as a challenge page with `cf-mitigated: challenge` on it. Every
// filter, sort, chart column and page link on /layoffs, and every type chip and
// filter on /events, is a Turbo fetch, so this listens for
// that one header on Turbo's responses instead of guessing in advance which
// click will be challenged. On a challenge it stops Turbo from rendering the
// challenge page, opens the dialog, draws the Turnstile widget and, once the
// reader passes, repeats the same request. Turnstile in pre-clearance mode is
// what sets Cloudflare's clearance cookie, so the repeat is let through.
//
// A reader who arrives at a filtered address directly, or without JavaScript,
// gets Cloudflare's full-page challenge instead, which ends in the same page.
// See Platform::Edge::HumanCheck.
export default class extends Controller {
  static targets = ["dialog", "widget", "status", "retry"]
  static values = { siteKey: String, scriptUrl: String }

  connect() {
    this.onResponse = this.onResponse.bind(this)
    this.closed = this.closed.bind(this)
    document.addEventListener("turbo:before-fetch-response", this.onResponse)
    // The dialog component owns the dialog's own data-action, so the close is
    // listened for here.
    this.dialogTarget.addEventListener("close", this.closed)
  }

  disconnect() {
    document.removeEventListener("turbo:before-fetch-response", this.onResponse)
    this.dialogTarget.removeEventListener("close", this.closed)
    this.reset()
  }

  onResponse(event) {
    const response = event.detail?.fetchResponse?.response
    if (!response || response.headers.get("cf-mitigated") !== "challenge") return

    // Turbo would otherwise draw Cloudflare's challenge page where the table
    // goes, or replace the whole page with it.
    event.preventDefault()

    const frame = event.target instanceof Element && event.target.tagName === "TURBO-FRAME" ? event.target : null
    this.pending = { url: response.url, frame: frame?.id }
    this.open()
  }

  open() {
    this.say("")
    this.retryTarget.hidden = true
    if (!this.dialogTarget.open) this.dialogTarget.showModal()

    this.loadTurnstile()
      .then((turnstile) => this.render(turnstile))
      .catch(() => this.fail("The check could not load. Check your connection and try again."))
  }

  // Loaded on the first challenge, never with the page: most readers of the
  // table never filter it, and they should not pay for a third party's script.
  loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile)

    if (!this.loading) {
      this.loading = new Promise((resolve, reject) => {
        const script = document.createElement("script")
        script.src = this.scriptUrlValue
        script.async = true
        script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error("no turnstile")))
        script.onerror = () => {
          this.loading = null
          script.remove()
          reject(new Error("turnstile did not load"))
        }
        document.head.appendChild(script)
      })
    }
    return this.loading
  }

  render(turnstile) {
    if (this.widgetId !== undefined) turnstile.remove(this.widgetId)

    this.widgetId = turnstile.render(this.widgetTarget, {
      sitekey: this.siteKeyValue,
      callback: () => this.passed(),
      "error-callback": () => this.fail("The check did not go through. Try it again."),
      "expired-callback": () => this.fail("The check ran out of time. Try it again."),
      "timeout-callback": () => this.fail("The check ran out of time. Try it again.")
    })
  }

  passed() {
    const pending = this.pending
    this.dialogTarget.close()
    if (!pending) return

    if (pending.frame) {
      window.Turbo.visit(pending.url, { frame: pending.frame, action: "advance" })
    } else {
      window.Turbo.visit(pending.url)
    }
  }

  retry() {
    this.open()
  }

  // Closing the dialog, by its button or by Escape, abandons the filter: the
  // page stays as it was.
  closed() {
    this.pending = null
    this.reset()
  }

  fail(message) {
    this.say(message)
    this.retryTarget.hidden = false
  }

  say(message) {
    this.statusTarget.textContent = message
  }

  reset() {
    if (window.turnstile && this.widgetId !== undefined) window.turnstile.remove(this.widgetId)
    this.widgetId = undefined
  }
}
